import { Fragment, useRef } from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import { ButtonBack, ButtonNext, Slide, Slider } from 'pure-react-carousel';
import { meta } from '../../../../meta';
import { getImagesByQuality, getQuality } from '../../../helpers';
import { ItemData } from '../../items/ItemData';
import { ArrowOne } from '../slider/arrows/ArrowOne';

export const ThumbnailsMainSlider = ({
  width,
  totalSlides,
  images,
  currentItemId,
  setCurrentItemId,
  sliderAnimationType,
  setMainIsDrag,
  mainIsDrag,
  sliderTransitionDuration,
  thumbnailsThumbnailsArrowsColor
}) => {
  const mainRef = useRef(null);
  const quality = getQuality(width);
  const currentImages = getImagesByQuality(images, quality);
  const isFade = sliderAnimationType === 'fade';

  if (!totalSlides) {
    return null;
  }

  return (
    <Fragment>
      <StyledSlider
        role="region"
        ref={mainRef}
        $duration={sliderTransitionDuration}
        classNameAnimation={classNames(
          `${meta.stylePrefix}-thumbnails-thumbnailsDelay`,
          {
            [`${meta.stylePrefix}-thumbnails-fadeAnimation`]: isFade
          }
        )}
        onTransitionEnd={() => {
          mainRef.current.instance.setState({
            deltaX: 0,
            deltaY: 0
          });
          setCurrentItemId(mainRef.current.instance.props.currentSlide);
          mainRef.current.instance.setState({ cancelNextClick: false });
        }}
        onMouseUp={() => {
          setMainIsDrag(
            mainRef.current && mainRef.current.instance.state.cancelNextClick
          );
        }}
      >
        {currentImages.map((item, index) => (
          <StyledSlide
            key={item.id}
            role="group"
            aria-selected={undefined}
            index={index}
            classNameHidden={classNames({
              [`${meta.stylePrefix}-thumbnails-slideHidden`]: isFade
            })}
            classNameVisible={classNames({
              [`${meta.stylePrefix}-thumbnails-slideVisible`]: isFade
            })}
            $duration={sliderTransitionDuration}
            $isVisible={currentItemId === index}
            $isFade={isFade}
          >
            <ItemData
              id={index}
              data={item}
              infoClassName={`${meta.stylePrefix}-thumbnails-info`}
              imageClassName={`${meta.stylePrefix}-thumbnails-image`}
              isDrag={mainIsDrag}
            />
          </StyledSlide>
        ))}
      </StyledSlider>

      <StyledButtonBack $arrowColor={thumbnailsThumbnailsArrowsColor}>
        <ArrowOne rotate />
      </StyledButtonBack>

      <StyledButtonNext $arrowColor={thumbnailsThumbnailsArrowsColor}>
        <ArrowOne />
      </StyledButtonNext>
    </Fragment>
  );
};

const StyledSlider = styled(Slider).attrs({
  className: `${meta.stylePrefix}-thumbnails-main`
})`
  width: 100%;
  position: relative;

  & .${meta.stylePrefix}-item-data-component {
    justify-content: flex-end;
  }

  & .${meta.stylePrefix}-info-component {
    text-align: center;
  }

  & .${meta.stylePrefix}-thumbnails-thumbnailsDelay {
    transition: transform ${({ $duration }) => $duration / 2}s;
  }

  & .${meta.stylePrefix}-thumbnails-fadeAnimation {
    transition-timing-function: steps(1, end) !important;
  }
`;

const StyledSlide = styled(Slide).attrs({
  className: `${meta.stylePrefix}-thumbnails-thumbnailsSlide`
})`
  transition: opacity ${({ $duration }) => $duration / 2}s !important;

  ${({ $isFade, $isVisible, $duration }) =>
    $isFade &&
    css`
      opacity: ${$isVisible ? 1 : 0};

      ${$isVisible &&
      css`
        transition: opacity ${$duration}s !important;
        transition-delay: ${$duration / 2}s !important;
      `}
    `}

  & .${meta.stylePrefix}-thumbnails-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const NavButtonStyles = css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;

  background-color: ${({ $arrowColor }) =>
    $arrowColor || 'rgba(136, 136, 136, 0.2)'};

  border-radius: 20px;
  border: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 1;
  transition: 0.3s ease opacity;
  z-index: 10;
  cursor: pointer;

  &:disabled {
    opacity: 0;
    pointer-events: none;
  }

  & svg {
    width: 20px;
    height: 20px;
  }
`;

const StyledButtonBack = styled(ButtonBack).attrs({
  className: classNames(
    `${meta.stylePrefix}-thumbnails-mainButton`,
    `${meta.stylePrefix}-thumbnails-mainButtonBack`
  )
})`
  ${NavButtonStyles};
  left: 10px;
`;

const StyledButtonNext = styled(ButtonNext).attrs({
  className: classNames(
    `${meta.stylePrefix}-thumbnails-mainButton`,
    `${meta.stylePrefix}-thumbnails-mainButtonNext`
  )
})`
  ${NavButtonStyles};
  right: 10px;
`;
